import {
  type Ref,
  ref,
  watch,
} from 'vue';
import {useQuasar} from 'quasar';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import {extractError} from '@/ts/composables/pure/use-loading-data-ref';
import {loggerInstance} from '@/ts/instances/logger-instance';
import {DISABLE_LOADING} from '@/ts/utils/consts';
import {AppError} from '@/ts/utils/errors';
import {ErrorCodesDto, type ErrorCode} from '@/ts/types/dto/error-codes-dto';
import {pagesPath} from '@/ts/router/pages-path';
import {sentryService} from '@/ts/services/sentry-service';

const logger = loggerInstance.getLogger('async');

function isConsentPendingCode(code: ErrorCode): boolean {
  return code === ErrorCodesDto.CONSENT_REQUIRED || code === ErrorCodesDto.CONSENT_NEEDS_REAPPROVAL;
}

/** Wraps an async callback with loading state and displays a Quasar notification on error */
export function useLoadingNotification<R, P extends any[]>(
  cb: (...args: P) => R extends undefined ? Promise<void> : Promise<R>,
  onSuccess?: (a: R, ...args: P) => void,
): [
  (...args: P) => Promise<void>,
  Ref<boolean>,
] {
  const loading: Ref<boolean> = ref<boolean>(false);
  const $q = useQuasar();
  const router = useRouter();
  const id = String(cb);
  const {t: $t} = useI18n();

  async function submitHandler(...args: P): Promise<void> {
    if (loading.value) {
      return;
    }
    loading.value = true;
    logger.trace(`Executing ${id}`)();
    let succeeded: boolean = false;
    let res: R | null = null;
    try {
      res = await cb(...args) as R;
      // eslint-disable-next-line require-atomic-updates
      loading.value = false;
      succeeded = true;
      logger.trace(`Successfully executed ${id}`)();
    } catch (err) {
      logger.error(`Failed ${id} because of`, err)();
      // eslint-disable-next-line require-atomic-updates
      loading.value = false;
      succeeded = false;
      if (err instanceof AppError && err.statusCode === ErrorCodesDto.NO_ACTIVE_LICENSES) {
        // Expected business flow — redirect, don't capture.
        void router.push({path: pagesPath.account.subscription, query: {reason: ErrorCodesDto.NO_ACTIVE_LICENSES}});
        return;
      }
      if (err instanceof AppError && isConsentPendingCode(err.statusCode)) {
        // Expected business flow too: the consent page asks for it. A toast would show the raw code,
        // and on a load with the consent pending every page that mounts before the redirect raises one.
        void router.push(pagesPath.main.consentRequired);
        return;
      }

      // AppErrors are captured by their producer (fetch-wrapper for API errors,
      // throw site for synchronous validation throws). Capturing here too would
      // double-count every API failure in Sentry. Only catch the unexpected.
      if (!(err instanceof AppError)) {
        sentryService.captureException(
          err instanceof Error ? err : new Error(String(err)),
          {origin: 'useLoadingNotification'},
        );
      }

      $q.notify({
        type: 'negative',
        message: extractError(err, $t),
      });
    }
    // move this thing out of try catch
    if (succeeded && onSuccess) {
      onSuccess(res!, ...args);
    }
  }

  return [submitHandler, loading];
}


/**
 * Proper way to handle any asynchronous operation, e.g. http request.
 * Upon error will call quasar notification.
 * Upon success will call cb.
 * Upon load will set {loading} state to true.
 * To start an asynchronous operation you have to call returned submitHandler
 *
 * @param cb will be called when you call submitHandler
 * @param onSuccess if specified, will be called upon successfully operation completion
 * @returns {loading} will be set to true/false when operation is in progress.
 *
 * @returns [submitHandler] is a trigger, that must be called to execute cb function argument
 * @example
 * const [submitHandler, loading] = useAsyncOperation(
 *   async(answers: CreateQuestionnaireAnswerDTO[]) => api.postOnboardQuestionnaireAnswers(answers),
 * );
 */
export function useLoadingSpinner<R, P extends any[]>(
  cb: (...args: P) => R extends undefined ? Promise<void> : Promise<R>,
  onSuccess?: (a: R, ...args: P) => void,
): [(...args: P) => Promise<void>, Ref<boolean>] {
  const $q = useQuasar();
  const [fn, loading] = useLoadingNotification(cb, (...args) => {
    if (!DISABLE_LOADING) {
      $q.loading.hide();
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/no-unused-expressions
    onSuccess && onSuccess(...args);
  });
  watch(loading, () => {
    if (!DISABLE_LOADING) {
      if (loading.value) {
        $q.loading.show();
      } else {
        $q.loading.hide();
      }
    }
  });

  return [fn, loading];
}
